import { useMemo } from 'react';
import { OfferStatus } from 'apps/commerce/popup_checkout/constants';
import { localizedPriceFromMinorUnit } from '../util';

/**
 * Custom hook to handle checkout text override logic and button text calculation
 *
 * @param {Object} formValues - The form values containing offer, price, offerPurchase, etc.
 * @param {Object} checkoutConfigData - Optional checkout configuration data with checkoutTextOverride settings
 * @param {Object} options - Additional options needed for button text calculation
 * @param {number} options.dueNowPriceInCents - The price due now in cents
 * @param {boolean} options.isFree - Whether the offer is free
 * @param {Function} options.t - Translation function
 * @returns {Object} An object containing the effective checkout text override and button text
 */
export const useCheckoutText = (formValues, checkoutConfigData = null, options = {}) => {
  const { dueNowPriceInCents, isFree, t } = options;
  const { offer, price, offerPurchase } = formValues || {};

  // Compute the effective checkout text override, prioritizing checkout config
  const effectiveCheckoutTextOverride = useMemo(() => {
    if (
      checkoutConfigData?.checkoutTextOverrideEnabled &&
      checkoutConfigData?.checkoutTextOverride
    ) {
      return checkoutConfigData.checkoutTextOverride;
    }
    return formValues?.checkoutTextOverride || null;
  }, [
    formValues?.checkoutTextOverride,
    checkoutConfigData?.checkoutTextOverride,
    checkoutConfigData?.checkoutTextOverrideEnabled,
  ]);

  // Determine if we have a checkout text override
  const hasCheckoutTextOverride = useMemo(
    () => Boolean(effectiveCheckoutTextOverride),
    [effectiveCheckoutTextOverride],
  );

  // Calculate the button text based on various conditions, and
  // apply any override value only after all status checks
  const buttonText = useMemo(() => {
    if (!t) return '';

    if (offerPurchase?.type === 'subscription') return t('form.already_subscribed');
    if (offer?.status === OfferStatus.SOLD_OUT) return t('form.sold_out');
    if (offer?.status === OfferStatus.EXPIRED) return t('form.expired');
    if (offer?.status === OfferStatus.DRAFT) return t('form.draft');
    if (effectiveCheckoutTextOverride?.length > 0) return effectiveCheckoutTextOverride;

    if (isFree) return t('form.submit_free');

    if (price && dueNowPriceInCents !== undefined) {
      const localizedDueNowPrice = localizedPriceFromMinorUnit(
        price.currency,
        dueNowPriceInCents,
        price.exponent,
      );
      return `${t('form.submit')} ${localizedDueNowPrice} ${price.currency.toUpperCase()}`;
    }

    return t('form.submit');
  }, [
    effectiveCheckoutTextOverride,
    offerPurchase?.type,
    offer?.status,
    isFree,
    price,
    dueNowPriceInCents,
    t,
  ]);

  return {
    effectiveCheckoutTextOverride,
    hasCheckoutTextOverride,
    buttonText,
  };
};

export default useCheckoutText;
